// §18 Video KYC — driven by the live state machine at
// GET /onboarding/applications/{id}/video-kyc. Start → checklist → consent →
// permissions → join → call → complete; the bank then audits
// (DONE_AUDIT_PENDING) and approves/rejects from the review desk. Also
// scheduling, link expiry (3 days) and 3-attempts-then-branch.
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import Modal from 'react-bootstrap/Modal';
import { getSession, getApplicationId } from '@/api/session';
import { getApplicationByReference } from '@/api/onboarding';
import {
  completeVideoKyc,
  getVideoKyc,
  joinVideoKyc,
  leaveVideoKyc,
  OUTCOME_MESSAGES,
  restartVideoKyc,
  scheduleVideoKyc,
  getVideoKycSlots,
  type VideoKycSlot,
  type VideoKycStatus,
} from '@/api/vkyc';
import { LOCATE_US } from '@/mock/data';
import './onboarding.css';

type Phase = 'start' | 'checklist' | 'consent' | 'permissions' | 'call' | 'schedule';

const CHECKLIST = [
  'Original PAN card',
  'Original Aadhaar card',
  'Blank white paper and a pen',
  'Good light and a stable internet connection',
  'I am physically in India right now',
];
const LANGUAGES = ['English', 'हिंदी', 'ಕನ್ನಡ', 'தமிழ்'];
const formatElapsed = (s: number): string =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const fmtSlot = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });

/** Next 5 bookable days (Mon–Sat); Sunday has no available slots. */
const nextWorkingDays = (): Date[] => {
  const days: Date[] = [];
  const d = new Date();
  while (days.length < 5) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) days.push(new Date(d));
  }
  return days;
};

const VideoKyc = () => {
  const navigate = useNavigate();
  const session = getSession();

  const [applicationId, setApplicationId] = useState<string | null>(getApplicationId());
  const [status, setStatus] = useState<VideoKycStatus | null>(null);
  const [loadError, setLoadError] = useState('');
  const [phase, setPhase] = useState<Phase>('start');
  const [busy, setBusy] = useState(false);

  // pre-call selections
  const [ticks, setTicks] = useState<boolean[]>(CHECKLIST.map(() => false));
  const [consentGiven, setConsentGiven] = useState(false);
  const [language, setLanguage] = useState('');
  const [permissions, setPermissions] = useState({ camera: false, microphone: false, location: false });

  // scheduling
  const [day, setDay] = useState<Date | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<VideoKycSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!applicationId || !day) return;
    const date = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    setSlotsLoading(true);
    setSlot(null);
    void getVideoKycSlots(applicationId, date)
      .then(setAvailableSlots)
      .catch(() => setAvailableSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [applicationId, day]);

  // the call
  const [promptIndex, setPromptIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);

  // Resolve the application id: the onboarding handle may be gone after
  // submit, so fall back to the tracking reference.
  useEffect(() => {
    if (applicationId || !session?.reference) return;
    getApplicationByReference(session.reference, session.mobileNumber)
      .then((a) => setApplicationId(a.applicationId))
      .catch(() => setLoadError('Could not load your Video KYC. Please try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async (id: string) => {
    try {
      setStatus(await getVideoKyc(id));
      setLoadError('');
    } catch {
      setLoadError('Could not load your Video KYC. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (applicationId) void refresh(applicationId);
  }, [applicationId, refresh]);

  // Call clock while on the call.
  useEffect(() => {
    if (phase !== 'call') return;
    const startedAt = Date.now();
    const clock = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 500);
    return () => clearInterval(clock);
  }, [phase]);

  if (!session?.reference && !getApplicationId()) {
    return <Navigate to="/applicant" replace />;
  }

  const prompts = [
    "Hello, I'm Priya from NovaBank. Please confirm your full name and date of birth.",
    'Please look straight at the camera.',
    'Show the front of your PAN card.',
    'Sign on the white paper and show it to the camera.',
    status?.question ?? 'One security question before we finish.',
    'Thank you. Your details will be reviewed by our team.',
  ];

  const doJoin = async () => {
    if (!applicationId) return;
    setBusy(true);
    try {
      setStatus(await joinVideoKyc(applicationId));
      setPromptIndex(0);
      setPhase('call');
    } catch {
      setLoadError('Could not connect you to an officer. Please try again.');
      setPhase('start');
    } finally {
      setBusy(false);
    }
  };

  const doComplete = async () => {
    if (!applicationId) return;
    setBusy(true);
    try {
      setStatus(await completeVideoKyc(applicationId));
    } finally {
      setBusy(false);
    }
  };

  const doLeave = async () => {
    setConfirmEnd(false);
    if (applicationId) await leaveVideoKyc(applicationId).catch(() => undefined);
    navigate('/applicant');
  };

  const doRestart = async () => {
    if (!applicationId) return;
    setBusy(true);
    try {
      setStatus(await restartVideoKyc(applicationId));
      setPhase('start');
    } finally {
      setBusy(false);
    }
  };

  const doSchedule = async () => {
    if (!applicationId || !day || !slot) return;
    setBusy(true);
    try {
      setStatus(await scheduleVideoKyc(applicationId, slot));
      setPhase('start');
      setDay(null);
      setSlot(null);
      setAvailableSlots([]);
    } catch {
      setLoadError('Could not book that slot. Please try another.');
    } finally {
      setBusy(false);
    }
  };

  // ---------- loading / error ----------
  if (!status && !loadError) {
    return (
      <div className="nova-ob-dark">
        <span className="spinner-border" aria-hidden="true" />
      </div>
    );
  }
  if (loadError && !status) {
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'center' }}>
          <p>{loadError}</p>
          <button type="button" className="btn btn-light w-100 mb-2" onClick={() => applicationId && void refresh(applicationId)}>
            Retry
          </button>
          <button type="button" className="btn btn-outline-light w-100" onClick={() => navigate('/applicant')}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  const state = status?.state ?? 'NOT_STARTED';
  const attemptsExhausted = (status?.attempts ?? 0) >= 3 && (state === 'FAILED' || state === 'REJECTED');

  // ---------- link expired ----------
  if (status?.linkExpired) {
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'center' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.5rem' }}>Link expired</h1>
          <p className="nova-ob-tagline">Video KYC links are valid for 3 days. Start again to get a new one.</p>
          <button type="button" className="btn btn-light w-100 fw-semibold" disabled={busy} onClick={doRestart}>
            Start again
          </button>
        </div>
      </div>
    );
  }

  // ---------- after 3 failed attempts ----------
  if (attemptsExhausted) {
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.5rem' }}>Please visit a branch</h1>
          <p className="nova-ob-tagline">
            Video KYC could not be completed after three attempts. Any NovaBank branch can finish your KYC in
            person. Carry your original PAN and Aadhaar.
          </p>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Find a branch</h2>
          <ul className="ps-3" style={{ fontSize: '0.85rem' }}>
            {LOCATE_US.filter((l) => l.kind === 'Branch').map((l) => (
              <li key={l.name} className="mb-2">{l.name} — {l.address}</li>
            ))}
          </ul>
          <button type="button" className="btn btn-light w-100 fw-semibold mt-2" onClick={() => navigate('/applicant')}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // ---------- done / approved ----------
  if (state === 'DONE_AUDIT_PENDING' || state === 'APPROVED') {
    const approved = state === 'APPROVED';
    return (
      <div className="nova-ob-success-screen vk-dark">
        <div className="nova-ob-tick" aria-hidden="true">✓</div>
        <h1 className="nova-ob-heading">Video KYC completed</h1>
        <p>
          {approved
            ? 'Your Video KYC is approved.'
            : 'Thank you. Your details will be reviewed by our team.'}
        </p>
        <div className="nova-ob-card w-100" style={{ maxWidth: 480 }}>
          <div className="nova-ob-tracker-step">
            <span className="nova-ob-tracker-dot done">✓</span>
            <span className="fw-semibold">Video KYC</span>
          </div>
          <div className="nova-ob-tracker-line" />
          <div className="nova-ob-tracker-step">
            <span className={`nova-ob-tracker-dot ${approved ? 'done' : 'current'}`}>{approved ? '✓' : '2'}</span>
            <span className={approved ? 'fw-semibold' : ''}>Verification in progress</span>
          </div>
          <div className="nova-ob-tracker-line" />
          <div className="nova-ob-tracker-step">
            <span className="nova-ob-tracker-dot">3</span>
            <span className="text-muted">Activated</span>
          </div>
        </div>
        <div className="nova-ob-footer">
          <div className="nova-ob-footer-inner">
            <button type="button" className="btn btn-outline-light" onClick={() => navigate('/applicant', { replace: true })}>
              End
            </button>
            <button type="button" className="btn btn-light fw-semibold" onClick={() => navigate('/applicant', { replace: true })}>
              Finish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- failed / rejected ----------
  if (state === 'FAILED' || state === 'REJECTED') {
    const message =
      (state === 'REJECTED' && status?.rejectionReason) ||
      OUTCOME_MESSAGES[status?.lastOutcome ?? ''] ||
      'The connection dropped during the call. Please find a stable network and try again.';
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'center' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.5rem' }}>
            {state === 'REJECTED' ? 'Video KYC was not approved' : 'We could not complete your Video KYC'}
          </h1>
          <p className="nova-ob-tagline">{message}</p>
          <p className="nova-ob-tagline">Attempt {status?.attempts ?? 1} of 3. Your link stays valid for 3 days.</p>
          <button type="button" className="btn btn-light w-100 fw-semibold mb-2" disabled={busy} onClick={doRestart}>
            Retry
          </button>
          <button type="button" className="btn btn-outline-light w-100" onClick={() => setPhase('schedule')}>
            Schedule for later instead
          </button>
        </div>
      </div>
    );
  }

  // ---------- the call ----------
  if (phase === 'call' || state === 'IN_PROGRESS') {
    const last = promptIndex >= prompts.length - 1;
    return (
      <div className="nova-vk-call">
        <div className="nova-vk-tile">
          <span className="nova-vk-timer">REC {formatElapsed(elapsed)}</span>
          <span className="nova-vk-live">● Good · New Delhi, IN</span>
          <div className="nova-vk-avatar" aria-hidden="true">P</div>
          <div className="nova-vk-officer-name">Priya · NovaBank Officer</div>
          <div className="nova-vk-self">You</div>
        </div>
        <p className="nova-vk-caption" aria-live="polite">{prompts[promptIndex]}</p>
        <div className="nova-vk-dots" aria-hidden="true">
          {prompts.map((_, i) => (
            <span key={i} className={i <= promptIndex ? 'active' : ''} />
          ))}
        </div>
        <div className="d-flex gap-2 nova-vk-end">
          <button type="button" className="btn btn-outline-light" onClick={() => setConfirmEnd(true)}>
            End
          </button>
          <button
            type="button"
            className="btn btn-light fw-semibold"
            disabled={busy}
            onClick={() => (last ? void doComplete() : setPromptIndex((i) => i + 1))}
          >
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            {last ? 'Finish' : "I've answered"}
          </button>
        </div>

        <Modal show={confirmEnd} onHide={() => setConfirmEnd(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>End the call?</Modal.Title>
          </Modal.Header>
          <Modal.Body>Your Video KYC will not be completed and you will need to start again.</Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmEnd(false)}>
              Stay
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void doLeave()}>
              End call
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // ---------- scheduling ----------
  if (phase === 'schedule') {
    const days = nextWorkingDays();
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
          <button type="button" className="nova-ob-back mb-3" aria-label="Back" onClick={() => setPhase('start')}>←</button>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>Schedule your call</h1>
          <p className="nova-ob-tagline">Pick a day and a bookable 30-minute slot.</p>
          <div className="nova-ob-label">Day</div>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {days.map((d) => (
              <button
                key={d.toDateString()}
                type="button"
                className={`nova-vk-chip ${day?.toDateString() === d.toDateString() ? 'selected' : ''}`}
                onClick={() => setDay(d)}
              >
                {d.toLocaleString('en-IN', { weekday: 'short' })}<br />
                {d.toLocaleString('en-IN', { day: 'numeric', month: 'short' })}
              </button>
            ))}
          </div>
          <div className="nova-ob-label">Slot</div>
          {slotsLoading && <p className="text-muted">Loading available slots…</p>}
          {!slotsLoading && day && availableSlots.length === 0 && <p className="text-muted">No slots are available for this day.</p>}
          <div className="d-flex gap-2 flex-wrap mb-3">
            {availableSlots.filter((s) => s.available).map((s) => (
              <button
                key={s.startsAt}
                type="button"
                className={`nova-vk-chip ${slot === s.startsAt ? 'selected' : ''}`}
                onClick={() => setSlot(s.startsAt)}
              >
                {new Date(s.startsAt).toLocaleString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-light w-100 fw-semibold" disabled={!day || !slot || slotsLoading || busy} onClick={() => void doSchedule()}>
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            Confirm slot
          </button>
        </div>
      </div>
    );
  }

  // ---------- booked ----------
  if (state === 'SCHEDULED' && phase === 'start') {
    const when = status?.scheduledAt ? new Date(status.scheduledAt) : null;
    const joinOpen = when ? Date.now() >= when.getTime() - 5 * 60_000 : false;
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'center' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.5rem' }}>Your Video KYC is booked</h1>
          <p className="nova-ob-tagline">{when ? fmtSlot(status!.scheduledAt!) : ''}</p>
          <p className="nova-ob-tagline">
            We have sent the details by SMS and email. Join opens five minutes before your slot.
          </p>
          <button
            type="button"
            className="btn btn-light w-100 fw-semibold mb-2"
            disabled={!joinOpen || busy}
            onClick={() => setPhase('checklist')}
          >
            Join
          </button>
          <button type="button" className="btn btn-outline-light w-100 mb-2" onClick={() => setPhase('schedule')}>
            Reschedule
          </button>
          <button type="button" className="btn btn-link text-white" onClick={() => navigate('/applicant')}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // ---------- pre-call: checklist ----------
  if (phase === 'checklist') {
    const allTicked = ticks.every(Boolean);
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>Before we begin</h1>
          <p className="nova-ob-tagline">Please confirm each of these.</p>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="vk-check-all"
              checked={ticks.every(Boolean)}
              onChange={(e) => setTicks(CHECKLIST.map(() => e.target.checked))}
            />
            <label className="form-check-label" htmlFor="vk-check-all">Select all</label>
          </div>
          {CHECKLIST.map((item, i) => (
            <div className="form-check mb-2" key={item}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`vk-check-${i}`}
                checked={ticks[i]}
                onChange={(e) => setTicks((t) => t.map((v, j) => (j === i ? e.target.checked : v)))}
              />
              <label className="form-check-label" htmlFor={`vk-check-${i}`}>{item}</label>
            </div>
          ))}
          <button type="button" className="btn btn-light w-100 fw-semibold mt-3" disabled={!allTicked} onClick={() => setPhase('consent')}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ---------- pre-call: consent ----------
  if (phase === 'consent') {
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>Your consent</h1>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="vk-consent"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="vk-consent">
              I consent to NovaBank recording this call and capturing my photo, PAN image, signature and location for KYC.
            </label>
          </div>
          <div className="nova-ob-label">Call language</div>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {LANGUAGES.map((l) => (
              <button key={l} type="button" className={`nova-vk-chip ${language === l ? 'selected' : ''}`} onClick={() => setLanguage(l)}>
                {l}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-light w-100 fw-semibold" disabled={!consentGiven || !language} onClick={() => setPhase('permissions')}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ---------- pre-call: permissions ----------
  if (phase === 'permissions') {
    const allAllowed = permissions.camera && permissions.microphone && permissions.location;
    const rows: Array<{ key: keyof typeof permissions; label: string }> = [
      { key: 'camera', label: 'Camera' },
      { key: 'microphone', label: 'Microphone' },
      { key: 'location', label: 'Location' },
    ];
    return (
      <div className="nova-ob-dark">
        <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
          <h1 className="nova-ob-brand" style={{ fontSize: '1.4rem' }}>Camera, microphone and location</h1>
          <p className="nova-ob-tagline">The officer needs to see and hear you, and confirm you are in India.</p>
          {rows.map((r) => (
            <div key={r.key} className="nova-vk-devicecheck mb-2">
              <span>{r.label} {permissions[r.key] ? '✓' : ''}</span>
              <button
                type="button"
                className="btn btn-outline-light btn-sm"
                disabled={permissions[r.key]}
                onClick={() => setPermissions((p) => ({ ...p, [r.key]: true }))}
              >
                {permissions[r.key] ? 'Allowed' : 'Allow'}
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-light w-100 fw-semibold mt-3" disabled={!allAllowed || busy} onClick={() => void doJoin()}>
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            Connect
          </button>
        </div>
      </div>
    );
  }

  // ---------- start (NOT_STARTED) ----------
  return (
    <div className="nova-ob-dark">
      <div className="nova-ob-card-dark" style={{ alignItems: 'stretch', textAlign: 'left' }}>
        <button type="button" className="nova-ob-back mb-3" aria-label="Back" onClick={() => navigate('/applicant')}>←</button>
        <div className="text-center mb-4">
          <h1 className="nova-ob-brand" style={{ fontSize: '1.5rem' }}>Complete your Video KYC</h1>
          <p className="nova-ob-tagline mb-0">A 3 to 5 minute video call with a NovaBank officer.</p>
        </div>
        <p className="text-center" style={{ fontSize: '0.85rem' }}>
          <span className="text-success fw-semibold">● Agents available now</span>
        </p>
        {loadError && <div className="alert alert-danger py-2">{loadError}</div>}
        <div className="nova-vk-devicecheck mb-3">
          <span>Keep your PAN, Aadhaar, a blank white sheet and a pen ready. You must be physically in India for the call.</span>
        </div>
        <button type="button" className="btn btn-light btn-lg w-100 fw-semibold mb-2" disabled={busy} onClick={() => setPhase('checklist')}>
          Start now
        </button>
        <button type="button" className="btn btn-outline-light w-100" onClick={() => setPhase('schedule')}>
          Schedule a call
        </button>
      </div>
    </div>
  );
};

export default VideoKyc;
